import React, { useMemo, useRef } from 'react';

import { AccountFieldDefinition } from '@flipdish/api-client-typescript';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import InputAdornment from '@mui/material/InputAdornment';
import TextField from '@mui/material/TextField';
import { type FieldProps, Field, useFormikContext } from 'formik';
import get from 'lodash/get';
import { Translate } from 'react-localize-redux';

import { formikValidate } from '../../../../../helpers/validation';
import { getFeatureFlagValue } from '../../../../../services/featureFlags/getFeatureFlagValue';
import { PortalFeatureFlags } from '../../../../../services/featureFlags/portalFeatureFlags';
import { useFeatureFlagValue } from '../../../../../services/featureFlags/useFeatureFlagValue';
import * as dynamicHelpers from '../bankAccFieldTextHelpers/DynamicLabel';
import FormItemLayout from '../FormItemLayout';
import BankAccountFormBussinesTypeFields from './BankAccountFormBussinesTypeFields';
import FieldWrapper from './FieldWrapper';
import { useBankAddressLookup } from './useBankAddressLookup';
import {
  type BankFormValues,
  type PopulatedAccountField,
  useBankCountryFields,
} from './useBankCountryFields';

export interface IPropsDynamicFields {
  countryFields: AccountFieldDefinition[] | undefined;
  /** Called when a GB sort-code lookup starts or finishes, so the parent can gate submission. */
  onLookupInProgressChange?: (inProgress: boolean) => void;
}
export type Props = IPropsDynamicFields;

const validate = (value: string, fieldName: string) => {
  return (
    formikValidate.required(value) || formikValidate.max_string_custom(100)(value.length, fieldName)
  );
};

const validateDynamic = (
  value: string,
  fieldName: string | undefined,
  maxLength: number | undefined,
  minLength: number | undefined
) => {
  return (
    formikValidate.required(value) ||
    (minLength &&
      fieldName &&
      formikValidate.min_string_custom(minLength)(value.length, fieldName)) ||
    (maxLength && fieldName && formikValidate.max_string_custom(maxLength)(value.length, fieldName))
  );
};

const DynamicFormFields = (props: Props) => {
  const { countryFields = [], onLookupInProgressChange } = props;
  const editView = window.location.pathname.endsWith('/edit');
  const { values } = useFormikContext<BankFormValues>();

  const gbFormImprovementsEnabled = useFeatureFlagValue(
    PortalFeatureFlags.bankAccount_gbFormImprovements,
    false
  );
  // All CE-1263 UI/lookup behaviour is scoped to flag on + GB only.
  const isGbFormImprovements = gbFormImprovementsEnabled && values.BankCountryCode === 'GB';

  const [disabledFieldsBeforeAccountValidation, setDisabledFieldsBeforeAccountValidation] =
    React.useState({
      bankName: false,
      bankAddress: false,
      swift: false,
      sortCode: false,
      accountNumber: false,
    });

  // Tracks whether Sort Code or Account Number has been modified with no lookup result yet.
  // Used alongside disabledFieldsBeforeAccountValidation.bankName (set at dispatch time by the
  // hook) to gate submit across the full window from first keystroke through to lookup completion.
  const [gbEntryModified, setGbEntryModified] = React.useState(false);

  const [loadingFieldsDuringAccountValidation, setLoadingFieldsDuringAccountValidation] =
    React.useState({
      bankName: false,
      bankAddress: false,
      swift: false,
      sortCode: false,
      accountNumber: false,
    });

  // GB feature: reorder to Sort Code → Account Number, then pass through anything else.
  // Zeus returns Account Number (key 2) before Sort Code (key 12), so we
  // explicitly reorder. Any other fields Zeus returns for GB (future additions) are
  // preserved via ...rest rather than an IBAN-specific guard, so this stays
  // field-name agnostic. Gated behind the bankAccount_gbFormImprovements flag.
  const effectiveCountryFields = useMemo(() => {
    if (isGbFormImprovements && countryFields.length > 0) {
      const sortCodeField = countryFields.find((f) => f.DisplayName === 'Sort Code');
      const accountNumberField = countryFields.find((f) => f.DisplayName === 'Account Number');
      if (sortCodeField && accountNumberField) {
        const rest = countryFields.filter(
          (f) => f.DisplayName !== 'Sort Code' && f.DisplayName !== 'Account Number'
        );
        return [sortCodeField, accountNumberField, ...rest];
      }
    }
    return countryFields;
  }, [isGbFormImprovements, countryFields]);

  // Reference to store the debounce timer so we can clear it on blur
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  const ibanLookupInlineErrorEnabled = getFeatureFlagValue(
    PortalFeatureFlags.bankAccount_ibanLookupInlineError,
    false
  );

  // IBAN minimum length for the currently selected country, taken directly from the Zeus
  // field definition. Zeus returns this correctly in production (e.g. 22 for IE). In
  // test/dev environments where the DB records aren't populated it will be undefined —
  // the length gate and inline error gate won't apply there, which is a data issue in
  // those environments, not a code issue (CE-1295).
  const ibanMinLength: number | undefined = React.useMemo(() => {
    if (!ibanLookupInlineErrorEnabled) return undefined;
    const ibanField = effectiveCountryFields.find((f) => f.DisplayName === 'IBAN');
    return ibanField?.MinLength ?? undefined;
  }, [ibanLookupInlineErrorEnabled, effectiveCountryFields]);

  const { triggerValidation, resetLookup, lookupStatus, lookupPath } = useBankAddressLookup(
    setDisabledFieldsBeforeAccountValidation,
    setLoadingFieldsDuringAccountValidation,
    debounceTimerRef,
    gbFormImprovementsEnabled,
    ibanMinLength
  );

  // Gate submit across two windows:
  // 1. Pre-debounce (first keystroke → 1 s debounce fires): gbEntryModified is set
  //    synchronously in onChange so Submit is disabled before the HTTP request starts.
  // 2. In-flight (dispatch → response): disabledFieldsBeforeAccountValidation.bankName
  //    is set by the hook at dispatch time.
  // Released on success OR failure; on failure Bank Name/Address unlock for manual entry.
  // Reset gbEntryModified via useEffect so it clears automatically on lookup completion
  // without needing the hook to call back into this component.
  // The cleanup always sends false so the parent unblocks on country-switch or unmount.
  React.useEffect(() => {
    if (lookupStatus === 'succeeded' || lookupStatus === 'failed') {
      setGbEntryModified(false);
    }
  }, [lookupStatus]);

  const gbLookupInProgress =
    isGbFormImprovements &&
    (gbEntryModified || disabledFieldsBeforeAccountValidation.bankName) &&
    lookupStatus !== 'succeeded';
  React.useEffect(() => {
    onLookupInProgressChange?.(gbLookupInProgress);
    return () => onLookupInProgressChange?.(false);
  }, [gbLookupInProgress, onLookupInProgressChange]);

  useBankCountryFields({
    countryFields: effectiveCountryFields,
    isGbFormImprovements,
    onCountryChange: resetLookup,
  });

  const renderDynamicContent = () => {
    const dynamicFields = effectiveCountryFields.map(function (dynamicField, index) {
      return (
        <Grid item xs={12} key={index}>
          <FieldWrapper>
            <Field
              name={`PopulatedAccountFields.${index}.Value`}
              validate={(value: string) => {
                if (
                  dynamicField.DisplayName === 'Swift' &&
                  disabledFieldsBeforeAccountValidation.swift
                ) {
                  return undefined;
                }
                // Return a Formik error when the IBAN country prefix doesn't match
                // the selected country — this blocks form submission in addition to
                // showing the inline helper text. Min/max-length errors are suppressed
                // while the mismatch holds (the mismatch message is more actionable).
                if (
                  ibanLookupInlineErrorEnabled &&
                  dynamicField.DisplayName === 'IBAN' &&
                  value &&
                  value.length >= 2 &&
                  values.BankCountryCode &&
                  value.slice(0, 2).toUpperCase() !== values.BankCountryCode.toUpperCase()
                ) {
                  return 'IBAN_Country_mismatch';
                }
                return validateDynamic(
                  value,
                  dynamicField.DisplayName,
                  // Zeus returns null min/max for IBAN fields; use Zeus-supplied MinLength
                  // as the Formik validation length when the flag is on (CE-1295).
                  dynamicField.MaxLength ??
                    (ibanLookupInlineErrorEnabled && dynamicField.DisplayName === 'IBAN'
                      ? ibanMinLength
                      : undefined),
                  dynamicField.MinLength ??
                    (ibanLookupInlineErrorEnabled && dynamicField.DisplayName === 'IBAN'
                      ? ibanMinLength
                      : undefined) ??
                    undefined
                );
              }}
            >
              {({ field, form }: FieldProps) => {
                const { errors, isSubmitting, touched } = form;
                const fieldError = get(errors, field.name) as string | undefined;

                const isSwiftField = dynamicField.DisplayName === 'Swift';
                const isSortCodeField = dynamicField.DisplayName === 'Sort Code';
                const isAccountNumberField = dynamicField.DisplayName === 'Account Number';

                // Suppress transient Required errors while lookup is in flight
                // (GB feature only — master never suppressed these).
                const suppressWhileLoading =
                  isGbFormImprovements &&
                  (isSortCodeField || isAccountNumberField) &&
                  lookupStatus === 'loading';
                const suppressWhenLocked =
                  isSwiftField && disabledFieldsBeforeAccountValidation.swift;
                const showError = editView
                  ? !!fieldError && !suppressWhileLoading && !suppressWhenLocked
                  : !!fieldError &&
                    !suppressWhileLoading &&
                    !suppressWhenLocked &&
                    (get(touched, field.name) as boolean | undefined);

                // GB: Sort Code and Account Number can be locked by a lookup in flight;
                // no Swift field exists for GB. Non-GB: Swift can be locked by IE lookup success.
                const fieldDisabled = isGbFormImprovements
                  ? isSortCodeField
                    ? isSubmitting || disabledFieldsBeforeAccountValidation.sortCode
                    : isAccountNumberField
                      ? isSubmitting || disabledFieldsBeforeAccountValidation.accountNumber
                      : isSubmitting
                  : isSwiftField
                    ? isSubmitting || disabledFieldsBeforeAccountValidation.swift
                    : isSubmitting;

                const showSpinner = isSwiftField && loadingFieldsDuringAccountValidation.swift;

                const sortCodeVal =
                  values.PopulatedAccountFields?.find(
                    (f: PopulatedAccountField) => f.DisplayName === 'Sort Code'
                  )?.Value || '';
                const accountNumberVal =
                  values.PopulatedAccountFields?.find(
                    (f: PopulatedAccountField) => f.DisplayName === 'Account Number'
                  )?.Value || '';
                const showSortCodeAccountLookupError =
                  isGbFormImprovements &&
                  isAccountNumberField &&
                  lookupStatus === 'failed' &&
                  lookupPath === 'sort_code' &&
                  sortCodeVal.length === 6 &&
                  accountNumberVal.length === 8;

                const ibanVal =
                  values.PopulatedAccountFields?.find(
                    (f: PopulatedAccountField) => f.DisplayName === 'IBAN'
                  )?.Value || '';
                // Inline lookup failure error for non-GB IBAN fields (IE — Republic of Ireland).
                // Flag-gated + Zeus MinLength. GB uses the Sort Code path, not IBAN.
                const isIbanField = dynamicField.DisplayName === 'IBAN';
                const ibanMeetsMinLength = !ibanMinLength || ibanVal.length >= ibanMinLength;
                const showIbanLookupFailedError =
                  isIbanField &&
                  lookupStatus === 'failed' &&
                  ibanLookupInlineErrorEnabled &&
                  ibanMeetsMinLength;

                // Show when the IBAN's country prefix doesn't match the selected country.
                // Takes priority over the lookup-failed error and suppresses length errors
                // (handled above in the validate prop).
                const showIbanCountryMismatch =
                  isIbanField &&
                  ibanLookupInlineErrorEnabled &&
                  ibanVal.length >= 2 &&
                  values.BankCountryCode !== '' &&
                  ibanVal.slice(0, 2).toUpperCase() !== values.BankCountryCode.toUpperCase();

                const shouldValidateOnBlur =
                  (!isGbFormImprovements && dynamicField.DisplayName === 'IBAN') ||
                  dynamicField.DisplayName === 'Sort Code' ||
                  dynamicField.DisplayName === 'Account Number';

                /** Normalises any IBAN field to uppercase so country-prefix checks are consistent. */
                const normaliseIbanInput = (value: string) =>
                  isIbanField ? value.toUpperCase() : value;

                const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
                  field.onBlur(e);

                  if (shouldValidateOnBlur) {
                    if (debounceTimerRef.current) {
                      clearTimeout(debounceTimerRef.current);
                      debounceTimerRef.current = null;
                    }
                    triggerValidation();
                  }
                };

                return (
                  <TextField
                    variant="standard"
                    {...field}
                    onChange={(e) => {
                      const newValue = normaliseIbanInput(e.target.value.replace(/\s/g, ''));
                      if (isGbFormImprovements && (isSortCodeField || isAccountNumberField)) {
                        form.setFieldValue(
                          `PopulatedAccountFields.${index}`,
                          {
                            Key: dynamicHelpers.getDynamicKey(dynamicField.Key),
                            DisplayName: dynamicField.DisplayName,
                            Value: newValue,
                          },
                          false
                        );
                        if (newValue) {
                          // Clear stale Bank Name/Address values so the user doesn't see
                          // results from a previous sort code. Bank Name/Address are NOT
                          // locked here — the hook locks them at dispatch time (after the
                          // 1 s debounce). gbEntryModified gates submit immediately so
                          // pressing Enter in the pre-debounce window is still blocked.
                          form.setFieldValue('BankName', '', false);
                          form.setFieldValue('BankAddress', '', false);
                          setGbEntryModified(true);
                        } else {
                          // Field cleared: reset the modified flag.
                          setGbEntryModified(false);
                        }
                      } else {
                        // Pre-CE-1263 / non-GB path: simple per-index write.
                        form.setFieldValue(`PopulatedAccountFields.${index}`, {
                          Key: dynamicHelpers.getDynamicKey(dynamicField.Key),
                          DisplayName: dynamicField.DisplayName,
                          Value: newValue,
                        });
                      }
                    }}
                    onBlur={handleBlur}
                    //@ts-ignore
                    label={<Translate id={`${dynamicHelpers.getDynamicLabel(dynamicField.Key)}`} />}
                    autoComplete="off"
                    fullWidth
                    disabled={fieldDisabled}
                    error={
                      showError ||
                      showSortCodeAccountLookupError ||
                      showIbanLookupFailedError ||
                      showIbanCountryMismatch
                    }
                    inputProps={{
                      'data-fd': `bank_dynamic_fields_${dynamicHelpers.getDynamicLabel(
                        dynamicField.Key
                      )}`,
                      ...(isSwiftField
                        ? {
                            'aria-busy': loadingFieldsDuringAccountValidation.swift,
                          }
                        : {}),
                    }}
                    InputProps={
                      showSpinner
                        ? {
                            endAdornment: (
                              <InputAdornment position="end">
                                <CircularProgress size={20} />
                              </InputAdornment>
                            ),
                          }
                        : undefined
                    }
                    InputLabelProps={{
                      shrink: true,
                    }}
                    helperText={
                      showSortCodeAccountLookupError ? (
                        <Translate id="Bank_account_GB_lookup_failed" />
                      ) : showIbanCountryMismatch ? (
                        <Translate id="IBAN_Country_mismatch" />
                      ) : showIbanLookupFailedError ? (
                        <Translate id="Bank_account_IBAN_lookup_failed" />
                      ) : showError ? (
                        <>{fieldError}</>
                      ) : undefined
                    }
                    margin="none"
                  />
                );
              }}
            </Field>
          </FieldWrapper>
        </Grid>
      );
    });
    return dynamicFields;
  };

  return (
    <FormItemLayout label={<Translate id="Bank_account" />}>
      {/* Persistent lookup lifecycle for a11y + E2E; not visual UI. */}
      <div
        data-fd="bank-account-lookup"
        aria-busy={lookupStatus === 'loading'}
        data-status={lookupStatus}
        hidden
      />
      {!editView && <BankAccountFormBussinesTypeFields />}
      {editView && (
        <FieldWrapper>
          <Field name={'BusinessType'}>
            {({ field }: FieldProps) => {
              return (
                <TextField
                  variant="standard"
                  {...field}
                  label={<Translate id={'Account_type'} />}
                  fullWidth
                  inputProps={{
                    'data-fd': 'Account_type',
                    readOnly: true,
                    disableUnderline: true,
                  }}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="none"
                />
              );
            }}
          </Field>
        </FieldWrapper>
      )}

      <FieldWrapper>
        <Field name={'AccountName'} validate={(value: string) => validate(value, 'Account Name')}>
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = errors[field.name] as string | undefined;
            const showError = editView
              ? !!fieldError
              : !!fieldError && (get(touched, field.name) as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id={'Account_name'} />}
                autoComplete="off"
                fullWidth
                disabled={isSubmitting}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_account_name',
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? fieldError : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>
      {renderDynamicContent()}

      <FieldWrapper>
        <Field
          name={'BankName'}
          validate={(value: string) =>
            disabledFieldsBeforeAccountValidation.bankName
              ? undefined
              : validate(value, 'Bank Name')
          }
        >
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = get(errors, field.name) as string | undefined;
            const bankNameLocked = disabledFieldsBeforeAccountValidation.bankName;
            const showError = editView
              ? !!fieldError && !bankNameLocked
              : !!fieldError &&
                !bankNameLocked &&
                (get(touched, field.name) as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id={'Bank_name'} />}
                autoComplete="off"
                fullWidth
                disabled={isSubmitting || disabledFieldsBeforeAccountValidation.bankName}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_name',
                  'aria-busy': loadingFieldsDuringAccountValidation.bankName,
                }}
                InputProps={
                  loadingFieldsDuringAccountValidation.bankName
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? <>{fieldError}</> : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>

      <FieldWrapper>
        <Field
          name={'BankAddress'}
          validate={(value: string) =>
            disabledFieldsBeforeAccountValidation.bankAddress
              ? undefined
              : validate(value, 'Address')
          }
        >
          {({ field, form }: FieldProps) => {
            const { errors, touched, isSubmitting } = form;
            const fieldError = errors[field.name] as string | undefined;
            const bankAddressLocked = disabledFieldsBeforeAccountValidation.bankAddress;
            const showError = editView
              ? !!fieldError && !bankAddressLocked
              : !!fieldError && !bankAddressLocked && (touched[field.name] as boolean | undefined);

            return (
              <TextField
                variant="standard"
                {...field}
                label={<Translate id="Bank_Address" />}
                autoComplete="off"
                fullWidth
                multiline
                maxRows={3}
                disabled={isSubmitting || disabledFieldsBeforeAccountValidation.bankAddress}
                error={showError}
                inputProps={{
                  'data-fd': 'Bank_address',
                  'aria-busy': loadingFieldsDuringAccountValidation.bankAddress,
                }}
                InputProps={
                  loadingFieldsDuringAccountValidation.bankAddress
                    ? {
                        endAdornment: (
                          <InputAdornment position="end">
                            <CircularProgress size={20} />
                          </InputAdornment>
                        ),
                      }
                    : undefined
                }
                InputLabelProps={{
                  shrink: true,
                }}
                helperText={showError ? fieldError : undefined}
                margin="none"
              />
            );
          }}
        </Field>
      </FieldWrapper>
    </FormItemLayout>
  );
};

export default DynamicFormFields;
